/* assets/collection-promo-grid.js */
(() => {
  const ROOT_SEL = "[data-cpg]";
  const FEATURE_CARD_SEL = ".cpg__feature-card";
  const PRODUCTS_GRID_SEL = ".cpg__products-grid";
  const PRODUCTS_SHELL_SEL = "[data-cpg-slider]";
  const TRACK_WRAP_SEL = "[data-cpg-track-wrap]";
  const TRACK_SEL = "[data-cpg-track]";
  const SLIDE_SEL = "[data-cpg-slide]";
  const REVEAL_TARGET_SEL = ".is-reveal-target";

  const DESKTOP_MQ = "(min-width: 750px)";
  const MOBILE_MQ = "(max-width: 749px)";
  const REDUCED_MOTION_MQ = "(prefers-reduced-motion: reduce)";

  const instances = new Map();

  const isDesktop = () => window.matchMedia(DESKTOP_MQ).matches;
  const isMobile = () => window.matchMedia(MOBILE_MQ).matches;
  const prefersReducedMotion = () => window.matchMedia(REDUCED_MOTION_MQ).matches;

  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
  const now = () => (window.performance && performance.now ? performance.now() : Date.now());

  class CollectionPromoGrid {
    constructor(root) {
      this.root = root;
      this.featureCard = root.querySelector(FEATURE_CARD_SEL);
      this.productsGrid = root.querySelector(PRODUCTS_GRID_SEL);
      this.productsShell = root.querySelector(PRODUCTS_SHELL_SEL);
      this.trackWrap = root.querySelector(TRACK_WRAP_SEL);
      this.track = root.querySelector(TRACK_SEL);
      this.slides = Array.from(root.querySelectorAll(SLIDE_SEL));
      this.revealTargets = Array.from(root.querySelectorAll(REVEAL_TARGET_SEL));

      this.count = this.slides.length;
      this.index = 0;
      this.tx = 0;
      this.slideW = 0;
      this.gap = 0;
      this.peekPx = 0;

      this._resizeRaf = null;
      this._io = null;
      this._ro = null;
      this._imageLoadHandlers = [];
      this._clickGuardTs = 0;

      this._drag = {
        down: false,
        pid: null,
        startX: 0,
        startY: 0,
        startTx: 0,
        moved: 0,
        movedAbs: 0,
        locked: false,
        t0: 0
      };

      this._onResize = this._onResize.bind(this);
      this._onPointerDown = this._onPointerDown.bind(this);
      this._onPointerMove = this._onPointerMove.bind(this);
      this._onPointerUp = this._onPointerUp.bind(this);
      this._onClickCapture = this._onClickCapture.bind(this);
      this._onDragStart = this._onDragStart.bind(this);

      this.init();
    }

    init() {
      this.syncFeatureHeight();
      this.bindResize();
      this.bindImageLoads();
      this.setupResizeObserver();
      this.setupReveal();
      this.setupDragPrevention();
      this.bindSlider();
      this.syncSlider();
    }

    bindResize() {
      window.addEventListener("resize", this._onResize, { passive: true });
    }

    bindImageLoads() {
      const images = Array.from(this.root.querySelectorAll("img"));

      images.forEach((img) => {
        const handler = () => {
          this.syncFeatureHeight();
          this.syncSlider();
        };

        if (img.complete) return;

        img.addEventListener("load", handler, { passive: true });
        img.addEventListener("error", handler, { passive: true });

        this._imageLoadHandlers.push({ img, handler });
      });
    }

    setupResizeObserver() {
      if (!("ResizeObserver" in window)) return;
      if (!this.featureCard) return;

      this._ro = new ResizeObserver(() => {
        this.syncFeatureHeight();
      });

      this._ro.observe(this.featureCard);
    }

    setupReveal() {
      const enabled = String(this.root.getAttribute("data-reveal-enabled")) === "true";
      if (!enabled || !this.revealTargets.length) return;

      if (prefersReducedMotion()) {
        this.revealTargets.forEach((target) => target.classList.add("is-revealed"));
        return;
      }

      this.root.classList.add("reveal-ready");

      if (!("IntersectionObserver" in window)) {
        this.revealTargets.forEach((target) => target.classList.add("is-revealed"));
        return;
      }

      this._io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add("is-revealed");
            this._io.unobserve(entry.target);
          });
        },
        {
          root: null,
          threshold: 0.12,
          rootMargin: "0px 0px -8% 0px"
        }
      );

      this.revealTargets.forEach((target) => {
        this._io.observe(target);
      });
    }

    setupDragPrevention() {
      this.root.addEventListener("dragstart", this._onDragStart);
    }

    _onDragStart(event) {
      const target = event.target;
      if (!target) return;

      if (
        target.closest("img") ||
        target.closest("picture") ||
        target.closest("a") ||
        target.closest("[data-cpg-no-drag]")
      ) {
        event.preventDefault();
      }
    }

    syncFeatureHeight() {
      if (!this.root || !this.productsGrid) return;

      this.root.style.removeProperty("--cpg-feature-h");
      this.productsGrid.style.removeProperty("height");
      this.productsGrid.style.removeProperty("min-height");

      if (!isDesktop()) return;

      if (!this.featureCard) return;

      const rect = this.featureCard.getBoundingClientRect();
      const featureHeight = Math.round(rect.height);

      if (!featureHeight || featureHeight < 1) return;

      this.root.style.setProperty("--cpg-feature-h", `${featureHeight}px`);
    }

    isMobileSliderActive() {
      if (!this.productsShell) return false;
      if (!isMobile()) return false;
      return this.productsShell.getAttribute("data-cpg-mobile-layout") === "slider";
    }

    bindSlider() {
      if (!this.trackWrap || !this.track || !this.count) return;

      this.trackWrap.addEventListener("pointerdown", this._onPointerDown, { passive: true });
      this.trackWrap.addEventListener("pointermove", this._onPointerMove, { passive: false });
      this.trackWrap.addEventListener("pointerup", this._onPointerUp, { passive: true });
      this.trackWrap.addEventListener("pointercancel", this._onPointerUp, { passive: true });
      this.trackWrap.addEventListener("click", this._onClickCapture, true);
    }

    readGap() {
      const styles = getComputedStyle(this.track);
      const raw = styles.columnGap || styles.gap || "0px";
      const parsed = parseFloat(String(raw).replace("px", "").trim());
      return Number.isFinite(parsed) ? parsed : 0;
    }

    syncSlider() {
      if (!this.productsShell || !this.trackWrap || !this.track || !this.count) return;

      if (!this.isMobileSliderActive()) {
        this.productsShell.classList.remove("is-slider-active", "is-dragging");
        this.productsShell.style.removeProperty("--cpg-slide-w");
        this.track.style.removeProperty("transform");
        this.track.style.removeProperty("transition");
        this.index = 0;
        this.tx = 0;
        return;
      }

      this.productsShell.classList.add("is-slider-active");

      const wrapRect = this.trackWrap.getBoundingClientRect();
      const wrapW = Math.round(wrapRect.width || 0);
      if (!wrapW) return;

      this.gap = this.readGap();

      const styles = getComputedStyle(this.root);
      const peekRaw = styles.getPropertyValue("--cpg-slider-peek-m").trim().replace("%", "");
      const peekPercent = parseFloat(peekRaw);
      const safePeekPercent = Number.isFinite(peekPercent) ? clamp(peekPercent, 0, 40) : 18;

      this.peekPx = Math.round((wrapW * safePeekPercent) / 100);
      this.slideW = Math.max(80, wrapW - this.peekPx);

      this.productsShell.style.setProperty("--cpg-slide-w", `${this.slideW}px`);
      this.index = clamp(this.index, 0, Math.max(0, this.count - 1));
      this._applyTx(this._calcTx(this.index), false);
    }

    _calcStep() {
      return this.slideW + this.gap;
    }

    _calcTx(index) {
      return -(index * this._calcStep());
    }

    _applyTx(tx, animate = true) {
      if (!this.track) return;

      if (!animate) {
        this.track.style.transition = "none";
      } else {
        this.track.style.transition = "";
      }

      this.track.style.transform = `translate3d(${tx}px, 0, 0)`;
      this.tx = tx;
    }

    _goTo(index, { animate = true } = {}) {
      if (!this.isMobileSliderActive()) return;
      const target = clamp(index, 0, Math.max(0, this.count - 1));
      this.index = target;
      this._applyTx(this._calcTx(target), animate);
    }

    _goBy(delta, { animate = true } = {}) {
      this._goTo(this.index + delta, { animate });
    }

    _onPointerDown(event) {
      if (!this.isMobileSliderActive()) return;
      if (this.count <= 1) return;
      if (event.pointerType === "mouse" && event.button !== 0) return;

      this._drag.down = true;
      this._drag.pid = event.pointerId;
      this._drag.startX = event.clientX;
      this._drag.startY = event.clientY;
      this._drag.startTx = this.tx;
      this._drag.moved = 0;
      this._drag.movedAbs = 0;
      this._drag.locked = false;
      this._drag.t0 = now();

      this.productsShell.classList.add("is-dragging");

      try {
        this.trackWrap.setPointerCapture(event.pointerId);
      } catch (_) {}
    }

    _onPointerMove(event) {
      if (!this._drag.down) return;
      if (this._drag.pid !== event.pointerId) return;
      if (!this.isMobileSliderActive()) return;

      const dx = event.clientX - this._drag.startX;
      const dy = event.clientY - this._drag.startY;

      this._drag.moved = dx;
      this._drag.movedAbs = Math.abs(dx);

      if (!this._drag.locked) {
        const adx = Math.abs(dx);
        const ady = Math.abs(dy);

        if (ady > adx && ady > 10) {
          this._cancelDrag(event.pointerId);
          return;
        }

        if (adx > 6) this._drag.locked = true;
      }

      if (!this._drag.locked) return;

      if (event.cancelable) event.preventDefault();

      const minTx = this._calcTx(this.count - 1);
      let nextTx = this._drag.startTx + dx;

      if (nextTx > 0) {
        nextTx = nextTx * 0.28;
      } else if (nextTx < minTx) {
        nextTx = minTx + (nextTx - minTx) * 0.28;
      }

      this._applyTx(nextTx, false);
    }

    _cancelDrag(pointerId) {
      this._drag.down = false;
      this.productsShell.classList.remove("is-dragging");

      try {
        this.trackWrap.releasePointerCapture(pointerId);
      } catch (_) {}

      this._applyTx(this._calcTx(this.index), true);
    }

    _onPointerUp(event) {
      if (!this._drag.down) return;
      if (this._drag.pid !== event.pointerId) return;

      if (!this.isMobileSliderActive()) {
        this._cancelDrag(event.pointerId);
        return;
      }

      this._drag.down = false;
      this.productsShell.classList.remove("is-dragging");

      try {
        this.trackWrap.releasePointerCapture(event.pointerId);
      } catch (_) {}

      const elapsed = Math.max(1, now() - this._drag.t0);
      const vx = (event.clientX - this._drag.startX) / elapsed;
      const threshold = Math.max(28, this._calcStep() * 0.18);

      let delta = 0;

      if (this._drag.movedAbs > threshold || Math.abs(vx) > 0.6) {
        delta = this._drag.moved < 0 ? 1 : -1;
      }

      if (delta !== 0) {
        this._goBy(delta, { animate: true });
        this._clickGuardTs = now();
      } else {
        const nearest = Math.round(-this.tx / this._calcStep());
        this._goTo(nearest, { animate: true });
      }
    }

    _onClickCapture(event) {
      if (!this.isMobileSliderActive()) return;

      const elapsed = now() - this._clickGuardTs;
      if (elapsed < 220 || this._drag.movedAbs > 8) {
        event.preventDefault();
        event.stopPropagation();
      }
    }

    _onResize() {
      if (this._resizeRaf) cancelAnimationFrame(this._resizeRaf);

      this._resizeRaf = requestAnimationFrame(() => {
        this.syncFeatureHeight();
        this.syncSlider();
      });
    }

    destroy() {
      window.removeEventListener("resize", this._onResize);

      if (this._resizeRaf) {
        cancelAnimationFrame(this._resizeRaf);
        this._resizeRaf = null;
      }

      if (this._io) {
        this._io.disconnect();
        this._io = null;
      }

      if (this._ro) {
        this._ro.disconnect();
        this._ro = null;
      }

      this._imageLoadHandlers.forEach(({ img, handler }) => {
        img.removeEventListener("load", handler);
        img.removeEventListener("error", handler);
      });
      this._imageLoadHandlers = [];

      if (this.trackWrap) {
        this.trackWrap.removeEventListener("pointerdown", this._onPointerDown);
        this.trackWrap.removeEventListener("pointermove", this._onPointerMove);
        this.trackWrap.removeEventListener("pointerup", this._onPointerUp);
        this.trackWrap.removeEventListener("pointercancel", this._onPointerUp);
        this.trackWrap.removeEventListener("click", this._onClickCapture, true);
      }

      this.root.removeEventListener("dragstart", this._onDragStart);
    }
  }

  const mount = (scope) => {
    const roots = Array.from(scope.querySelectorAll(ROOT_SEL));

    roots.forEach((root) => {
      if (instances.has(root)) return;
      instances.set(root, new CollectionPromoGrid(root));
    });
  };

  const unmount = (scope) => {
    const roots = Array.from(scope.querySelectorAll(ROOT_SEL));

    roots.forEach((root) => {
      const instance = instances.get(root);
      if (!instance) return;
      instance.destroy();
      instances.delete(root);
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => mount(document));
  } else {
    mount(document);
  }

  document.addEventListener("shopify:section:load", (event) => {
    if (!event.target) return;
    mount(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    if (!event.target) return;
    unmount(event.target);
  });

  document.addEventListener("shopify:section:reorder", () => {
    instances.forEach((instance) => {
      instance.syncFeatureHeight();
      instance.syncSlider();
    });
  });

  document.addEventListener("shopify:section:select", (event) => {
    if (!event.target) return;

    const roots = Array.from(event.target.querySelectorAll(ROOT_SEL));
    roots.forEach((root) => {
      const instance = instances.get(root);
      if (!instance) return;
      instance.syncFeatureHeight();
      instance.syncSlider();
    });
  });
})();